import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Stack,
  Skeleton,
  Checkbox,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import { RestartAlt } from "@mui/icons-material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import {
  Add,
  People,
  Mail,
  Phone,
  Label,
  MoreVert,
  Edit,
  Delete,
  Upload,
  ViewModule,
  TableChart,
  PersonAdd,
  FileDownload,
  History,
  DoubleArrow,
  Visibility,
} from "@mui/icons-material";
import { Menu } from "@mui/material";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useFetch } from "services/hooks/useFetch";
import ContactDetails from "components/contacts/ContactDetails";
import CloseIcon from "@mui/icons-material/Close";
import ContactImport from "components/contacts/ContactImport";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  setFrajunPhoneNumber,
  setFrajunContactName,
  setFrajunAutoInitiate,
  setVisible,
  setGlobalSearchValue,
} from "services/Redux/userToken";
import {
  childBoxStyleSpaceBetween,
  doubleArrowStyle,
  drawerStyle,
  parentBoxStyle,
} from "services/constants";
import ContactFormPage from "./ContactFormPage";
import EditContactPage from "./EditContactPage";
import { crmContactColors } from "./contactFormStyles";
import LogActivityDialog from "components/contacts/LogActivityDialog";
import { useContactDrawer } from "components/contacts/GlobalContactDrawer";

const toISODate = (d) => format(d, "yyyy-MM-dd");

const ACTIVITY_DATE_SHORTCUTS = [
  {
    label: "Today",
    getRange: () => {
      const today = toISODate(new Date());
      return [today, today];
    },
  },
  {
    label: "Yesterday",
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const yesterday = toISODate(d);
      return [yesterday, yesterday];
    },
  },
  {
    label: "This week",
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      const dayOfWeek = (now.getDay() + 6) % 7; // Monday = 0
      start.setDate(now.getDate() - dayOfWeek);
      return [toISODate(start), toISODate(now)];
    },
  },
];

function getPrimaryEmail(contact) {
  return contact.emails?.find((e) => e.primary)?.email || contact.emails?.[0]?.email;
}
function getPrimaryPhone(contact) {
  return contact.phone_numbers?.find((p) => p.primary)?.number || contact.phone_numbers?.[0]?.number;
}

function getRelativeTime(dateString) {
  if (!dateString) return null;
  const diffDays = Math.floor((Date.now() - new Date(dateString).getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return "1 week ago";
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return "1 month ago";
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

function getPrimaryLocation(contact) {
  if (!contact.addresses || contact.addresses.length === 0) return null;
  const work = contact.addresses.find((a) => a.type === "work");
  const addr = work || contact.addresses[0];
  const parts = [addr.city, addr.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

const getColdBorderColor = (lastActivityDate) => {
  if (!lastActivityDate) return 'transparent';
  const days = Math.floor(
    (Date.now() - new Date(lastActivityDate).getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days > 21) return '#A32D2D';
  if (days > 14) return '#BA7517';
  return 'transparent';
};

const CSV_COLUMNS = [
  { key: "company",   label: "Account",    getValue: (c) => c.company || "" },
  { key: "name",      label: "Name",       getValue: (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim() },
  { key: "email",     label: "Email",      getValue: (c) => getPrimaryEmail(c) || "" },
  { key: "phone",     label: "Phone",      getValue: (c) => getPrimaryPhone(c) || "" },
  { key: "industry_type", label: "Sector", getValue: (c) => c.industry_type || "" },
  { key: "employee_count", label: "Employee Count", getValue: (c) => c.employee_count ?? "" },
  { key: "location",  label: "Location",   getValue: (c) => getPrimaryLocation(c) || "" },
  { key: "agent",     label: "Agent",      getValue: (c) => c.username || "" },
  { key: "status",    label: "Status",     getValue: (c) => c.status_name || "" },
  { key: "source",    label: "Source",     getValue: (c) => c.source_name || "" },
  { key: "score",     label: "Score",      getValue: (c) => c.score != null ? String(c.score) : "" },
  { key: "createdat", label: "Created At", getValue: (c) => c.createdat ? format(new Date(c.createdat), "yyyy-MM-dd HH:mm") : "" },
];

const crmFormDrawerStyle = {
  ...drawerStyle,
  "& .MuiDrawer-paper": {
    ...drawerStyle["& .MuiDrawer-paper"],
    width: { xs: "100vw", sm: "820px", md: "880px" },
    maxWidth: "100vw",
    backgroundColor: crmContactColors.page,
    boxShadow: "-18px 0 40px rgba(31, 41, 51, 0.18)",
  },
};

const crmActionButtonSx = {
  backgroundColor: crmContactColors.action,
  borderColor: crmContactColors.action,
  color: "#10231D",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: crmContactColors.actionHover,
    borderColor: crmContactColors.actionHover,
    boxShadow: "0 6px 14px rgba(24, 140, 90, 0.18)",
  },
  "&.Mui-disabled": {
    backgroundColor: "#DCEBE6",
    borderColor: "#DCEBE6",
    color: "#7A8A85",
  },
};

function escapeCSVField(value) {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCSV(rows, columns) {
  const header = columns.map((c) => escapeCSVField(c.label)).join(",");
  const body = rows.map((row) => columns.map((col) => escapeCSVField(col.getValue(row))).join(","));
  return [header, ...body].join("\n");
}

export default function Contacts() {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [viewMode, setViewMode] = useState("table");
  const [sortConfig, setSortConfig] = useState({ key: "first_name", direction: "asc" });
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeCallInProgress, setActiveCallInProgress] = useState(
    Boolean(localStorage.getItem("active_call_uuid"))
  );

  // ── BULK SELECTION STATE ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAgentDialogOpen, setBulkAgentDialogOpen] = useState(false);
  const [bulkAgent, setBulkAgent] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkStatusMenuAnchor, setBulkStatusMenuAnchor] = useState(null);
  const [pendingBulkStatus, setPendingBulkStatus] = useState(null);
  const [bulkStatusConfirmOpen, setBulkStatusConfirmOpen] = useState(false);
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);

  const { registerPageDrawer } = useContactDrawer();
  useEffect(() => {
    registerPageDrawer(true);
    return () => registerPageDrawer(false);
  }, [registerPageDrawer]);

  useEffect(() => {
    const refreshActiveCall = () => {
      setActiveCallInProgress(Boolean(localStorage.getItem("active_call_uuid")));
    };
    refreshActiveCall();
    window.addEventListener("storage", refreshActiveCall);
    const interval = setInterval(refreshActiveCall, 2500);
    return () => {
      window.removeEventListener("storage", refreshActiveCall);
      clearInterval(interval);
    };
  }, []);

  const [sourceFilter, setSourceFilter] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [sortValue, setSortValue] = useState("first_name_asc");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contactFormDrawer, setContactFormDrawer] = useState({
    open: false,
    mode: "create",
    contactId: null,
  });
  const [filterCount, setFilterCount] = useState(0);
  const [tempFilters, setTempFilters] = useState({
    status: "all",
    source: "all",
    agent: userDetails?.id || "all",
    dateFrom: '',
    dateTo: '',
    location: "all",
    industryType: "",
  });
  const [locationFilter, setLocationFilter] = useState("all");
  const [industryTypeFilter, setIndustryTypeFilter] = useState("");
  const globalSearch = useSelector((state) => state.tokenReducer.globalSearchValue || "");
  const dispatch = useDispatch();
  const history = useHistory();
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, contact: null });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuContactId, setMenuContactId] = useState(null);
  const [rowStatusMenuAnchor, setRowStatusMenuAnchor] = useState(null);
  const [rowStatusMenuContactId, setRowStatusMenuContactId] = useState(null);
  const [rowAgentMenuAnchor, setRowAgentMenuAnchor] = useState(null);
  const [rowAgentMenuContactId, setRowAgentMenuContactId] = useState(null);
  const { result: users } = useFetch(`${domain}crm/crm-members/active/?company_id=${requestCompanyId}`);
  const [agentFilter, setAgentFilter] = useState(userDetails?.id || "all");
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [logActivityOpen, setLogActivityOpen] = useState(false);
  const [logActivityContact, setLogActivityContact] = useState(null);
  const [logActivityPretype, setLogActivityPretype] = useState(null);
  const [selectedCsvColumns, setSelectedCsvColumns] = useState(() => CSV_COLUMNS.map((c) => c.key));
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef(null);
  const location = useLocation();

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
    setSortValue(`${key}_${direction}`);
  };

  const filterContacts = React.useCallback(() => {
    setFilteredContacts(contacts);
  }, [contacts]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setPage(0);
      setDebouncedSearch(value);
    }, 300);
  };

  const [locationOptions, setLocationOptions] = React.useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contacts/cities/`);
        if (res.ok) setLocationOptions(res.responseDetails || []);
      } catch (err) {}
    };
    if (domain) fetchCities();
  }, [domain]);

  const sortedContacts = filteredContacts;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setCurrentPage(newPage + 1);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setCurrentPage(1);
  };

  const handleCallClick = async (contact) => {
    const primaryPhone = getPrimaryPhone(contact);
    if (activeCallInProgress) { toast.warning("A call is already active."); return; }
    if (!primaryPhone) { toast.error("No phone number found for this contact"); return; }
    if (!primaryPhone.startsWith("+91")) { toast.error(`Phone number must have country code +91.`); return; }
    const contactName = contact.company || `${contact.first_name} ${contact.last_name}`;
    dispatch(setFrajunPhoneNumber(primaryPhone));
    dispatch(setFrajunContactName(contactName));
    dispatch(setFrajunAutoInitiate(false));
    dispatch(setVisible(true));
    toast.success(`Initiating call to ${contactName}`);
    try {
      await customAPI(`${domain}crm/activities/`, "POST", {
        type: 1, title: `Call Initiated: ${contactName}`,
        description: `Outbound call to ${primaryPhone}`,
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"), contact: contact.id,
      });
    } catch (error) {}
  };

  const handleExportCSV = () => {
    const cols = CSV_COLUMNS.filter((c) => selectedCsvColumns.includes(c.key));
    if (cols.length === 0) { toast.error("Please select at least one column"); return; }
    const csvContent = buildCSV(sortedData, cols);
    const dateStr = format(new Date(), "yyyy-MM-dd_HH-mm-ss");
    const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `contacts_export_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setCsvDialogOpen(false);
  };

  useEffect(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (sourceFilter !== "all") count++;
    if (agentFilter !== "all") count++;
    if (dateFrom || dateTo) count++;
    if (locationFilter !== "all") count++;
    if (industryTypeFilter.trim()) count++;
    setFilterCount(count);
  }, [statusFilter, sourceFilter, agentFilter, dateFrom, dateTo, locationFilter, industryTypeFilter]);

  const loadContacts = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (agentFilter !== "all") {
        if (String(agentFilter) === String(userDetails?.id)) {
          params.append('mine', 'true');
        } else {
          params.append('agent', agentFilter);
        }
      }
      if (statusFilter && statusFilter !== "all") params.append('status', statusFilter);
      if (sourceFilter && sourceFilter !== "all") params.append('source', sourceFilter);
      const effectiveSearch = (globalSearch || '').trim() || (debouncedSearch || '').trim();
      if (effectiveSearch) params.append('search', effectiveSearch);
      if (dateFrom) params.append('activity_date_from', dateFrom);
      if (dateTo) params.append('activity_date_to', dateTo);
      if (locationFilter && locationFilter !== "all") params.append('city', locationFilter);
      if (industryTypeFilter.trim()) params.append('industry_type', industryTypeFilter.trim());
      const ORDERING_MAP = {
        first_name: 'first_name',
        company: 'company',
        createdat: 'createdat',
        last_activity: 'last_activity_date',
        status: 'status',
      };
      const orderKey = ORDERING_MAP[sortConfig.key];
      if (orderKey) {
        params.append('ordering', sortConfig.direction === 'desc' ? `-${orderKey}` : orderKey);
      }
      params.append('page', currentPage);
      params.append('page_size', rowsPerPage);
      const response = await customGetAPI(`${domain}crm/contacts/?${params.toString()}`);
      const data = response.responseDetails;
      const results = data?.results ?? (Array.isArray(data) ? data : []);
      setContacts(results);
      setTotalCount(data?.count ?? results.length);
      setHasNextPage(!!data?.next);
    } catch (error) {
      setContacts([]);
      setTotalCount(0);
      setHasNextPage(false);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [domain, agentFilter, userDetails?.id, statusFilter, sourceFilter, debouncedSearch, globalSearch, dateFrom, dateTo, locationFilter, industryTypeFilter, currentPage, rowsPerPage, sortConfig]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  useEffect(() => {
    if (location.state?.contactId && contacts.length > 0) {
      const found = contacts.find((c) => c.id === location.state.contactId);
      if (found) setSelectedContact(found);
    }
  }, [location.state, contacts]);

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-status/?page_size=100`);
        setStatusOptions(res.responseDetails?.results || []);
      } catch (err) {}
    };
    const fetchSourceOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-source/?page_size=100`);
        setSourceOptions(res.responseDetails?.results || []);
      } catch (err) {}
    };
    fetchStatusOptions();
    fetchSourceOptions();
  }, [domain]);

  useEffect(() => {
    filterContacts();
  }, [contacts, globalSearch, filterContacts]);

  const handleDeleteContact = async () => {
    if (!deleteModal.contact) return;
    try {
      const response = await customAPI(`${domain}crm/contacts/${deleteModal.contact.id}/`, "PATCH", { isdeleted: true });
      if (response.code === 200) {
        toast.success("Contact deleted.");
        setContacts((prev) => prev.filter((c) => c.id !== deleteModal.contact.id));
        if (selectedContact?.id === deleteModal.contact.id) setSelectedContact(null);
      }
      setDeleteModal({ isOpen: false, contact: null });
    } catch (err) {}
  };

  const openMenu = (event, contactId) => { setMenuAnchorEl(event.currentTarget); setMenuContactId(contactId); };
  const closeMenu = () => { setMenuAnchorEl(null); setMenuContactId(null); };

  const openCreateContactDrawer = React.useCallback(() => {
    setContactFormDrawer({ open: true, mode: "create", contactId: null });
  }, []);

  const openEditContactDrawer = React.useCallback((contactId) => {
    setContactFormDrawer({ open: true, mode: "edit", contactId });
  }, []);

  const closeContactFormDrawer = React.useCallback(() => {
    setContactFormDrawer((prev) => ({ ...prev, open: false }));
  }, []);

  const handleContactFormSaved = React.useCallback(async ({ keepOpen = false, contact: savedContact = null } = {}) => {
    await loadContacts();
    if (savedContact?.id && selectedContact?.id === savedContact.id) {
      setSelectedContact(savedContact);
    }
    if (!keepOpen) {
      closeContactFormDrawer();
    }
  }, [loadContacts, selectedContact?.id, closeContactFormDrawer]);

  const openRowStatusMenu = (e, contactId) => {
    setRowStatusMenuAnchor(e.currentTarget);
    setRowStatusMenuContactId(contactId);
    closeMenu();
  };
  const openRowAgentMenu = (e, contactId) => {
    setRowAgentMenuAnchor(e.currentTarget);
    setRowAgentMenuContactId(contactId);
    closeMenu();
  };
  const handleRowStatusChange = async (status) => {
    const contactId = rowStatusMenuContactId;
    setRowStatusMenuAnchor(null);
    setRowStatusMenuContactId(null);
    try {
      await customAPI(`${domain}crm/contacts/${contactId}/`, "PATCH", { status: status.id });
      toast.success("Status updated");
      loadContacts();
    } catch (err) {}
  };
  const handleRowAgentChange = async (agent) => {
    const contactId = rowAgentMenuContactId;
    setRowAgentMenuAnchor(null);
    setRowAgentMenuContactId(null);
    try {
      await customAPI(`${domain}crm/contacts/${contactId}/`, "PATCH", { assigned_to: agent.id });
      toast.success("Agent reassigned");
      loadContacts();
    } catch (err) {}
  };

  const stringToColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
  };

  const menuProps = { PaperProps: { style: { maxHeight: 250, overflowY: "auto" } } };

  const mappedContacts = sortedContacts.map((contact) => {
    const agentUser = users?.find((u) => u.id === contact.assigned_to);
    return { ...contact, agent_image_url: agentUser?.image_url || null };
  });

  const handleOpenDrawer = () => {
    setTempFilters({ status: statusFilter, source: sourceFilter, agent: agentFilter, dateFrom: dateFrom, dateTo: dateTo, location: locationFilter, industryType: industryTypeFilter });
    setDrawerOpen(true);
  };

  const handleApplyFilters = () => {
    setStatusFilter(tempFilters.status);
    setSourceFilter(tempFilters.source);
    setAgentFilter(tempFilters.agent);
    setDateFrom(tempFilters.dateFrom || '');
    setDateTo(tempFilters.dateTo || '');
    setLocationFilter(tempFilters.location || "all");
    setIndustryTypeFilter(tempFilters.industryType || "");
    setCurrentPage(1);
    setPage(0);
    setDrawerOpen(false);
  };

  const handleResetTempFilters = () => {
    setTempFilters({ status: "all", source: "all", agent: userDetails?.id || "all", dateFrom: '', dateTo: '', location: "all", industryType: "" });
    handleResetAllFilters();
    setDrawerOpen(false);
  };

  const handleResetAllFilters = () => {
    setStatusFilter("all"); setSourceFilter("all");
    setAgentFilter(userDetails?.id || "all"); setDateFrom(''); setDateTo('');
    setLocationFilter("all"); setSearchTerm(""); setDebouncedSearch("");
    setIndustryTypeFilter("");
    dispatch(setGlobalSearchValue(""));
    setCurrentPage(1); setPage(0);
  };

  const findRec = (id) => mappedContacts.find((c) => c.id === id) || null;

  const sortedData = mappedContacts;

  // ── BULK SELECTION HANDLERS ───────────────────────────────────────────────
  const pagedData = sortedData;
  const allPageSelected = pagedData.length > 0 && pagedData.every((c) => selectedIds.includes(c.id));
  const somePageSelected = pagedData.some((c) => selectedIds.includes(c.id)) && !allPageSelected;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...pagedData.map((c) => c.id)])]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => !pagedData.find((c) => c.id === id)));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBulkAssignAgent = async () => {
    if (!bulkAgent) { toast.error("Please select an agent"); return; }
    setBulkAssigning(true);
    try {
      await customAPI(`${domain}crm/contacts/bulk-assign/`, "PATCH", {
        contact_ids: selectedIds,
        agent_id: bulkAgent.id,
      });
      toast.success(`Agent assigned to ${selectedIds.length} contact(s).`);
      setSelectedIds([]);
      setBulkAgent(null);
      setBulkAgentDialogOpen(false);
      loadContacts();
    } catch (err) {
      console.error('[Contacts] operation failed:', err);
      toast.error("Bulk assignment failed");
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!pendingBulkStatus || selectedIds.length === 0) return;
    setBulkStatusUpdating(true);
    try {
      const response = await customAPI(`${domain}crm/contacts/bulk-status/`, "POST", {
        contact_ids: selectedIds,
        status_id: pendingBulkStatus.id,
      });
      if (response.ok) {
        setContacts((prev) =>
          prev.map((c) =>
            selectedIds.includes(c.id)
              ? { ...c, status: pendingBulkStatus.id, status_name: pendingBulkStatus.label, status_color: pendingBulkStatus.color }
              : c
          )
        );
        toast.success(`Status updated for ${response.responseDetails?.updated_count ?? selectedIds.length} contact(s)`);
        setSelectedIds([]);
        setBulkStatusConfirmOpen(false);
        setPendingBulkStatus(null);
      }
    } finally {
      setBulkStatusUpdating(false);
    }
  };

  return (
    <Box p={4}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={selectedContact ? 8 : 12}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: 1 }}
          >
            <Box flex={1}><Typography variant="h4">Contacts</Typography></Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center"
              sx={{ overflowX: { xs: "auto", sm: "visible" }, "&::-webkit-scrollbar": { display: "none" } }}
            >
              <TextField
                size="small"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ minWidth: 200 }}
              />
              <Button variant="outlined" onClick={() => setShowImport(true)}
                sx={crmActionButtonSx}
                startIcon={<Upload />}>Import</Button>
              <Button variant="outlined"
                onClick={() => setCsvDialogOpen(true)}
                disabled={sortedData.length === 0}
                startIcon={<FileDownload />}
                sx={crmActionButtonSx}>
                Export CSV</Button>
              <Button variant="contained" onClick={openCreateContactDrawer}
                startIcon={<Add />}
                sx={crmActionButtonSx}>
                Add Contact</Button>
              <IconButton onClick={handleOpenDrawer} title="Filters">
                <Tooltip slotProps={tooltipStyleProps} title={"Filters"} arrow>
                  <Badge badgeContent={filterCount} color="primary"><FilterListIcon /></Badge>
                </Tooltip>
              </IconButton>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortValue} onChange={(e) => {
                  const value = e.target.value;
                  const lastUnderscore = value.lastIndexOf("_");
                  const key = value.substring(0, lastUnderscore);
                  const direction = value.substring(lastUnderscore + 1);
                  setSortValue(value); setSortConfig({ key, direction });
                }} label="Sort By" MenuProps={menuProps}>
                  <MenuItem value="company_asc">Account (A–Z)</MenuItem>
                  <MenuItem value="company_desc">Account (Z–A)</MenuItem>
                  <MenuItem value="first_name_asc">First Name (A–Z)</MenuItem>
                  <MenuItem value="first_name_desc">First Name (Z–A)</MenuItem>
                  <MenuItem value="createdat_desc">Created (Newest)</MenuItem>
                  <MenuItem value="createdat_asc">Created (Oldest)</MenuItem>
                </Select>
              </FormControl>
              <Mui.ToggleButtonGroup value={viewMode} exclusive
                onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }} size="small">
                <Tooltip arrow slotProps={tooltipStyleProps} title="Card View">
                  <Mui.ToggleButton value="card"><ViewModule fontSize="small" /></Mui.ToggleButton>
                </Tooltip>
                <Tooltip arrow slotProps={tooltipStyleProps} title="Table View">
                  <Mui.ToggleButton value="table"><TableChart fontSize="small" /></Mui.ToggleButton>
                </Tooltip>
              </Mui.ToggleButtonGroup>
              <Tooltip arrow slotProps={tooltipStyleProps} title="Reset Filters">
                <IconButton onClick={handleResetAllFilters}><RestartAlt /></IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Filter Drawer */}
          <Grid container spacing={2} mb={2}>
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
              ModalProps={{ BackdropProps: { onClick: (e) => e.stopPropagation(), style: { background: "transparent" } }, disableEscapeKeyDown: true }}>
              <Box sx={{ width: 350, padding: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Advanced Filters</Typography>
                  <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
                </Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select MenuProps={menuProps} sx={{ borderRadius: "12px" }} value={tempFilters.status}
                    onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })} label="Status">
                    <MenuItem value="all">All</MenuItem>
                    {statusOptions.map((s) => <MenuItem key={s.id} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Source</InputLabel>
                  <Select MenuProps={menuProps} sx={{ borderRadius: "12px" }} value={tempFilters.source}
                    onChange={(e) => setTempFilters({ ...tempFilters, source: e.target.value })} label="Source">
                    <MenuItem value="all">All</MenuItem>
                    {sourceOptions.map((s) => <MenuItem key={s.id} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Agent</InputLabel>
                  <Select MenuProps={menuProps} value={tempFilters.agent}
                    onChange={(e) => setTempFilters({ ...tempFilters, agent: e.target.value })} label="Agent"
                    sx={{ borderRadius: "12px" }}>
                    {userDetails && (
                      <MenuItem value={userDetails.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={userDetails.image_url} sx={{ width: 24, height: 24 }}>
                            {userDetails.username?.[0]?.toUpperCase()}
                          </Avatar>
                          <Typography>Me ({userDetails.username})</Typography>
                        </Box>
                      </MenuItem>
                    )}
                    <MenuItem value="all">All Agents</MenuItem>
                    {users?.filter((u) => String(u.id) !== String(userDetails?.id)).map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={user.image_url} sx={{ width: 24, height: 24 }}>
                            {user.username?.[0]?.toUpperCase()}
                          </Avatar>
                          <Typography>{user.username}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Last activity date
                </Typography>
                <Box display="flex" gap={1} sx={{ mb: 1 }}>
                  <TextField
                    type="date"
                    label="From"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tempFilters.dateFrom}
                    onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                  />
                  <TextField
                    type="date"
                    label="To"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tempFilters.dateTo}
                    onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                  />
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
                  {ACTIVITY_DATE_SHORTCUTS.map((shortcut) => (
                    <Chip
                      key={shortcut.label}
                      label={shortcut.label}
                      size="small"
                      clickable
                      onClick={() => {
                        const [from, to] = shortcut.getRange();
                        setTempFilters({ ...tempFilters, dateFrom: from, dateTo: to });
                      }}
                    />
                  ))}
                </Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>City</InputLabel>
                  <Select MenuProps={menuProps} value={tempFilters.location ?? "all"}
                    onChange={(e) => setTempFilters({ ...tempFilters, location: e.target.value })}
                    label="Location" sx={{ borderRadius: "12px" }} renderValue={(val) => (val === "all" ? "All" : val)}>
                    <MenuItem value="all">All</MenuItem>
                    {locationOptions.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Sector"
                  value={tempFilters.industryType || ""}
                  onChange={(e) => setTempFilters({ ...tempFilters, industryType: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={1} mt={3}>
                  <Button variant="outlined" onClick={handleResetTempFilters} fullWidth startIcon={<RefreshIcon />}>Reset</Button>
                  <Button sx={crmActionButtonSx}
                    variant="contained" fullWidth onClick={handleApplyFilters}>Apply</Button>
                </Box>
              </Box>
            </Drawer>
          </Grid>

          <Box mt={2} />

          {/* ── BULK ACTION BAR ─────────────────────────────────────────────── */}
          {selectedIds.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.2,
                mb: 1,
                borderRadius: 1,
                backgroundColor: "rgba(132, 222, 184, 0.18)",
                border: `1px solid ${crmContactColors.borderStrong}`,
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {selectedIds.length} record(s) selected
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PersonAdd fontSize="small" />}
                  onClick={() => setBulkAgentDialogOpen(true)}
                  sx={crmActionButtonSx}
                >
                  Assign Agent
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Label fontSize="small" />}
                  onClick={(e) => setBulkStatusMenuAnchor(e.currentTarget)}
                  sx={crmActionButtonSx}
                >
                  Change Status
                </Button>
                <Menu
                  anchorEl={bulkStatusMenuAnchor}
                  open={Boolean(bulkStatusMenuAnchor)}
                  onClose={() => setBulkStatusMenuAnchor(null)}
                  PaperProps={{ style: { maxHeight: 260, overflowY: "auto" } }}
                >
                  {statusOptions.map((s) => (
                    <MenuItem
                      key={s.id}
                      onClick={() => {
                        setPendingBulkStatus(s);
                        setBulkStatusMenuAnchor(null);
                        setBulkStatusConfirmOpen(true);
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: s.color || "#ccc", flexShrink: 0 }} />
                        {s.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={() => setSelectedIds([])}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}

          {viewMode === "card" ? (
            <Grid container spacing={2}>
              {isLoading ? (
                Array.from(new Array(rowsPerPage)).map((_, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Skeleton variant="circular" width={44} height={44} />
                          <Box flex={1}>
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="60%" />
                          </Box>
                        </Box>
                        <Box mt={2} display="flex" gap={1}>
                          <Skeleton variant="rectangular" width={60} height={24} />
                          <Skeleton variant="rectangular" width={80} height={24} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : filteredContacts.length > 0 ? (
                sortedData.map((contact) => {
                  const contactPhone = getPrimaryPhone(contact);
                  const canCallContact = Boolean(contactPhone && contactPhone.startsWith("+91")) && !activeCallInProgress;
                  return (
                    <Grid item xs={12} key={contact.id}>
                      <Card onClick={() => setSelectedContact(contact)}
                        sx={{
                          cursor: "pointer",
                          border: selectedContact?.id === contact.id ? `2px solid ${crmContactColors.accent}` : "1px solid #e0e0e0",
                          borderRadius: 2,
                          boxShadow: selectedContact?.id === contact.id ? "0 4px 16px rgba(0, 212, 170, 0.16)" : "0 2px 8px rgba(0,0,0,0.06)",
                          transition: "0.2s ease", position: "relative",
                          "&::before": {
                            content: '""', position: "absolute", left: 0, top: 0,
                            height: "100%", width: "4px",
                            backgroundColor: selectedContact?.id === contact.id ? crmContactColors.accent : "transparent",
                            borderTopLeftRadius: "inherit", borderBottomLeftRadius: "inherit",
                          },
                        }}>
                        <CardContent sx={{ px: 3, py: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar sx={{ bgcolor: stringToColor(contact.company ? contact.company : `${contact.first_name} ${contact.last_name}`), width: 44, height: 44, fontWeight: 600 }}>
                                {contact.company ? contact.company[0]?.toUpperCase() : `${contact.first_name?.[0]?.toUpperCase() || ""}${contact.last_name?.[0]?.toUpperCase() || ""}`}
                              </Avatar>
                              <Box>
                                <Typography fontWeight={600} fontSize="1rem">
                                  {contact.company ? contact.company : `${contact.first_name} ${contact.last_name}`}
                                </Typography>
                                <Box display="flex" gap={2} fontSize={13} color="text.secondary" flexWrap="wrap">
                                  {getPrimaryEmail(contact) && <Box display="flex" alignItems="center" gap={0.5}><Mail fontSize="small" /> {getPrimaryEmail(contact)}</Box>}
                                  {contactPhone && (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); if (canCallContact) handleCallClick(contact); }}
                                        disabled={!canCallContact} sx={{ p: 0.5, color: canCallContact ? "success.main" : "text.disabled" }}>
                                        <Phone fontSize="small" />
                                      </IconButton>
                                      <Typography variant="body2">{contactPhone}</Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label={contact.status_name} sx={{ backgroundColor: contact.status_color, color: "#fff" }} size="small" />
                              <IconButton onClick={(e) => { e.stopPropagation(); openMenu(e, contact.id); }}>
                                <MoreVert />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>

                      <Menu anchorEl={menuAnchorEl} open={menuContactId === contact.id} onClose={closeMenu} onClick={(e) => e.stopPropagation()}>
                        <MenuItem onClick={() => { history.push(`/admin/crm/contacts/${contact.id}`); closeMenu(); }}>
                          <Visibility fontSize="small" sx={{ mr: 1 }} /> View profile
                        </MenuItem>
                        <MenuItem onClick={() => { closeMenu(); setLogActivityContact(findRec(contact.id)); setLogActivityPretype(null); setLogActivityOpen(true); }}>
                          <History fontSize="small" sx={{ mr: 1 }} /> Log activity
                        </MenuItem>
                        <MenuItem onClick={() => { closeMenu(); setLogActivityContact(findRec(contact.id)); setLogActivityPretype({ value: "call", label: "Call" }); setLogActivityOpen(true); }}>
                          <Phone fontSize="small" sx={{ mr: 1 }} /> Log call
                        </MenuItem>
                        <Mui.Divider />
                        <MenuItem onClick={() => { openEditContactDrawer(contact.id); closeMenu(); }}>
                          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit contact
                        </MenuItem>
                        <MenuItem onClick={(e) => { openRowStatusMenu(e, contact.id); }}>
                          <Label fontSize="small" sx={{ mr: 1 }} /> Change status
                        </MenuItem>
                        <MenuItem onClick={(e) => { openRowAgentMenu(e, contact.id); }}>
                          <PersonAdd fontSize="small" sx={{ mr: 1 }} /> Reassign agent
                        </MenuItem>
                        <Mui.Divider />
                        <MenuItem onClick={() => { setDeleteModal({ isOpen: true, contact }); closeMenu(); }} sx={{ color: "#A32D2D" }}>
                          <Delete fontSize="small" sx={{ mr: 1, color: "#A32D2D" }} /> Delete contact
                        </MenuItem>
                      </Menu>
                    </Grid>
                  );
                })
              ) : (
                <Grid item xs={12} textAlign="center" py={4}>
                  <People fontSize="large" sx={{ color: "gray" }} />
                  <Typography variant="h6">No contacts found</Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            // ── TABLE VIEW ──────────────────────────────────────────────────
            <ContactsTable
              data={sortedData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleSort={handleSort}
              sortConfig={sortConfig}
              setSelectedContact={setSelectedContact}
              openMenu={openMenu}
              menuAnchorEl={menuAnchorEl}
              menuContactId={menuContactId}
              closeMenu={closeMenu}
              getPrimaryEmail={getPrimaryEmail}
              getPrimaryPhone={getPrimaryPhone}
              history={history}
              setDeleteModal={setDeleteModal}
              statusFilter={statusFilter}
              sourceFilter={sourceFilter}
              agentFilter={agentFilter}
              dateFilter={dateFilter}
              searchTerm={searchTerm}
              selectedIds={selectedIds}
              onSelectOne={handleSelectOne}
              onSelectAll={handleSelectAll}
              allPageSelected={allPageSelected}
              somePageSelected={somePageSelected}
              statusOptions={statusOptions}
              users={users}
              openRowStatusMenu={openRowStatusMenu}
              openRowAgentMenu={openRowAgentMenu}
              onOpenEditContact={openEditContactDrawer}
              onLogActivity={(contact) => { setLogActivityContact(contact); setLogActivityPretype(null); setLogActivityOpen(true); }}
              onLogCall={(contact) => { setLogActivityContact(contact); setLogActivityPretype({ value: "call", label: "Call" }); setLogActivityOpen(true); }}
            />
          )}

          {viewMode === "card" && totalCount > 0 && (
            <Mui.TablePagination className="custom-pagination" component="div"
              rowsPerPageOptions={[10, 25, 50, 100]} count={totalCount}
              rowsPerPage={rowsPerPage} page={page}
              onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 3, ".MuiTablePagination-toolbar": { justifyContent: "flex-end" } }} />
          )}
        </Grid>

        {selectedContact && (
          <Grid item xs={12} md={4} sx={{ position: "sticky", top: 16, alignSelf: "flex-start", maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}>
            <ContactDetails contact={selectedContact} onClose={() => setSelectedContact(null)}
              onUpdateContact={(updatedContact) => {
                if (updatedContact && updatedContact.id) {
                  setContacts((prev) => prev.map((c) => c.id === updatedContact.id ? updatedContact : c));
                  setSelectedContact(updatedContact);
                }
              }}
              onEditContact={openEditContactDrawer} />
          </Grid>
        )}
      </Grid>

      {showImport && (
        <ContactImport open={showImport} onClose={() => setShowImport(false)}
          onImportSuccess={() => { setShowImport(false); loadContacts(); }} users={users} />
      )}

      <Drawer
        anchor="right"
        open={contactFormDrawer.open}
        onClose={closeContactFormDrawer}
        sx={crmFormDrawerStyle}
      >
        <Box sx={parentBoxStyle}>
          <Mui.Paper
            elevation={0}
            sx={{
              backgroundColor: "#fff",
              borderBottom: `1px solid ${crmContactColors.border}`,
              borderRadius: 0,
            }}
          >
            <Box sx={{ ...childBoxStyleSpaceBetween, px: 1.5, pt: 1 }}>
              <IconButton
                onClick={closeContactFormDrawer}
                sx={{
                  ...doubleArrowStyle,
                  color: crmContactColors.accentDark,
                  "&:hover": { backgroundColor: "rgba(132, 222, 184, 0.18)" },
                }}
              >
                <DoubleArrow />
              </IconButton>
            </Box>
            <Box sx={{ ml: 2.2, mr: 2, mb: 1.5, borderLeft: `4px solid ${crmContactColors.accent}`, pl: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: crmContactColors.text }}>
                {contactFormDrawer.mode === "edit" ? "Edit Contact" : "Add Contact"}
              </Typography>
              <Typography variant="body2" sx={{ color: crmContactColors.muted }}>
                {contactFormDrawer.mode === "edit"
                  ? "Update contact information, communication details, and CRM ownership."
                  : "Create a contact with account, phone, email, address, and CRM details."}
              </Typography>
            </Box>
          </Mui.Paper>
          <Box sx={{ flexGrow: 1, overflow: "auto", p: 2, pb: 0, backgroundColor: crmContactColors.page }}>
            {contactFormDrawer.mode === "edit" ? (
              <EditContactPage
                key={`edit-${contactFormDrawer.contactId || "blank"}`}
                contactId={contactFormDrawer.contactId}
                drawerMode
                hideHeader
                onClose={closeContactFormDrawer}
                onSaved={handleContactFormSaved}
              />
            ) : (
              <ContactFormPage
                key="create-contact"
                drawerMode
                hideHeader
                onClose={closeContactFormDrawer}
                onSaved={handleContactFormSaved}
              />
            )}
          </Box>
        </Box>
      </Drawer>

      <DeleteConfirmModal open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, contact: null })}
        onConfirm={handleDeleteContact}
        contactName={deleteModal.contact ? `${deleteModal.contact.first_name} ${deleteModal.contact.last_name}` : ""} />

      {/* ── BULK AGENT ASSIGN DIALOG ────────────────────────────────────── */}
      <Dialog open={bulkAgentDialogOpen} onClose={() => { if (!bulkAssigning) { setBulkAgentDialogOpen(false); setBulkAgent(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          Assign Agent
          <IconButton size="small" onClick={() => { setBulkAgentDialogOpen(false); setBulkAgent(null); }} disabled={bulkAssigning}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assigning agent to <strong>{selectedIds.length}</strong> selected contact(s).
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={bulkAgent?.id || ""}
              onChange={(e) => {
                const user = users?.find((u) => u.id === e.target.value);
                setBulkAgent(user || null);
              }}
              label="Select Agent"
              MenuProps={menuProps}
              renderValue={(val) => {
                const u = users?.find((x) => x.id === val);
                if (!u) return "Select Agent";
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={u.image_url} sx={{ width: 24, height: 24 }}>{u.username?.[0]?.toUpperCase()}</Avatar>
                    <Typography variant="body2">{u.username}</Typography>
                  </Box>
                );
              }}
            >
              {users?.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={user.image_url} sx={{ width: 28, height: 28 }}>{user.username?.[0]?.toUpperCase()}</Avatar>
                    <Typography variant="body2">{user.username}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => { setBulkAgentDialogOpen(false); setBulkAgent(null); }} disabled={bulkAssigning}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkAssignAgent} disabled={bulkAssigning || !bulkAgent}
            startIcon={bulkAssigning ? <Mui.CircularProgress size={16} /> : <PersonAdd />}
            sx={crmActionButtonSx}>
            {bulkAssigning ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── BULK STATUS CONFIRM DIALOG ─────────────────────────────────────── */}
      <Dialog
        open={bulkStatusConfirmOpen}
        onClose={() => { if (!bulkStatusUpdating) { setBulkStatusConfirmOpen(false); setPendingBulkStatus(null); } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          Change Status
          <IconButton size="small" onClick={() => { setBulkStatusConfirmOpen(false); setPendingBulkStatus(null); }} disabled={bulkStatusUpdating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Change status of <strong>{selectedIds.length}</strong> contact{selectedIds.length !== 1 ? "s" : ""} to{" "}
            <strong style={{ color: pendingBulkStatus?.color }}>{pendingBulkStatus?.label}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => { setBulkStatusConfirmOpen(false); setPendingBulkStatus(null); }}
            disabled={bulkStatusUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkStatusUpdate}
            disabled={bulkStatusUpdating}
            startIcon={bulkStatusUpdating ? <Mui.CircularProgress size={16} /> : <Label />}
            sx={crmActionButtonSx}
          >
            {bulkStatusUpdating ? "Updating…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Row-level status sub-menu ─────────────────────────────────────── */}
      <Menu
        anchorEl={rowStatusMenuAnchor}
        open={Boolean(rowStatusMenuAnchor)}
        onClose={() => { setRowStatusMenuAnchor(null); setRowStatusMenuContactId(null); }}
        PaperProps={{ style: { maxHeight: 260, overflowY: "auto" } }}
      >
        {statusOptions.map((s) => (
          <MenuItem key={s.id} onClick={() => handleRowStatusChange(s)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: s.color || "#ccc", flexShrink: 0 }} />
              {s.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* ── Row-level agent sub-menu ──────────────────────────────────────── */}
      <Menu
        anchorEl={rowAgentMenuAnchor}
        open={Boolean(rowAgentMenuAnchor)}
        onClose={() => { setRowAgentMenuAnchor(null); setRowAgentMenuContactId(null); }}
        PaperProps={{ style: { maxHeight: 260, overflowY: "auto" } }}
      >
        {users?.map((u) => (
          <MenuItem key={u.id} onClick={() => handleRowAgentChange(u)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar src={u.image_url} sx={{ width: 24, height: 24 }}>{u.username?.[0]?.toUpperCase()}</Avatar>
              {u.username}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <LogActivityDialog
        open={logActivityOpen}
        onClose={() => setLogActivityOpen(false)}
        contact={logActivityContact}
        preselectedType={logActivityPretype}
        onSaved={() => {
          setLogActivityOpen(false);
          loadContacts();
        }}
      />

      {/* ── CSV Export Column Selector ──────────────────────────────────────── */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          Export CSV
          <IconButton size="small" onClick={() => setCsvDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select columns to include ({sortedData.length} contact{sortedData.length !== 1 ? "s" : ""})
          </Typography>
          <Box display="flex" gap={1} mb={1}>
            <Button size="small" onClick={() => setSelectedCsvColumns(CSV_COLUMNS.map((c) => c.key))}>Select All</Button>
            <Button size="small" onClick={() => setSelectedCsvColumns([])}>Deselect All</Button>
          </Box>
          <Mui.FormGroup>
            {CSV_COLUMNS.map((col) => (
              <Mui.FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    checked={selectedCsvColumns.includes(col.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCsvColumns((prev) => [...prev, col.key]);
                      } else {
                        setSelectedCsvColumns((prev) => prev.filter((k) => k !== col.key));
                      }
                    }}
                    sx={{ "&.Mui-checked": { color: crmContactColors.accent } }}
                  />
                }
                label={col.label}
              />
            ))}
          </Mui.FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportCSV}
            disabled={selectedCsvColumns.length === 0}
            startIcon={<FileDownload />}
            sx={crmActionButtonSx}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Delete confirm modal ──────────────────────────────────────────────────────
const DeleteConfirmModal = ({ open, onClose, onConfirm, contactName }) => {
  const handleClose = (event, reason) => { if (reason !== "backdropClick" && reason !== "escapeKeyDown") onClose(); };
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Delete Contact
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete <strong>{contactName}</strong>? This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained">Delete</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Last Activity cell ────────────────────────────────────────────────────────
const LastActivityCell = ({ date, type }) => {
  if (!date) {
    return <Typography variant="body2" color="text.secondary">No activity yet</Typography>;
  }
  const diffDays = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  const color = diffDays > 21 ? "#A32D2D" : diffDays > 14 ? "#BA7517" : "inherit";
  return (
    <Box>
      <Typography variant="body2" sx={{ color }}>{getRelativeTime(date)}</Typography>
      {type && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <History sx={{ fontSize: 12 }} />{type}
        </Typography>
      )}
    </Box>
  );
};

// ── Contacts Table with checkboxes ────────────────────────────────────────────
const ContactsTable = ({
  data, page, rowsPerPage, totalCount, handleChangePage, handleChangeRowsPerPage,
  handleSort, sortConfig, setSelectedContact, openMenu, menuAnchorEl,
  menuContactId, closeMenu, getPrimaryEmail, getPrimaryPhone, history,
  setDeleteModal, statusFilter, sourceFilter, agentFilter, dateFilter, searchTerm,
  selectedIds, onSelectOne, onSelectAll, allPageSelected, somePageSelected,
  statusOptions, users, openRowStatusMenu, openRowAgentMenu, onOpenEditContact,
  onLogActivity, onLogCall,
}) => {
  const NON_SORTABLE = new Set(["email", "phone", "agent", "location", "actions"]);

  const columns = [
    { key: "company", label: "Account" },
    { key: "first_name", label: "First Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "industry_type", label: "Sector" },
    { key: "employee_count", label: "Employees" },
    { key: "location", label: "Location" },
    { key: "agent", label: "Agent" },
    { key: "status", label: "Status" },
    { key: "last_activity", label: "Last Activity" },
    { key: "createdat", label: "Created At" },
    { key: "actions", label: "Actions" },
  ];

  return (
    <Mui.TableContainer component={Mui.Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
      <Mui.Table>
        <Mui.TableHead>
          <Mui.TableRow>
            {/* Checkbox column */}
            <Mui.TableCell padding="checkbox" sx={{ width: 48 }}>
              <Checkbox
                indeterminate={somePageSelected}
                checked={allPageSelected}
                onChange={onSelectAll}
                sx={{ "&.Mui-checked": { color: crmContactColors.accent }, "&.MuiCheckbox-indeterminate": { color: crmContactColors.accent } }}
              />
            </Mui.TableCell>
            {columns.map((col) => {
              const sortable = !NON_SORTABLE.has(col.key);
              return (
                <Mui.TableCell key={col.key}
                  onClick={sortable ? () => handleSort(col.key) : undefined}
                  sx={{ fontWeight: 600, cursor: sortable ? "pointer" : "default", whiteSpace: "nowrap", userSelect: "none" }}>
                  {col.label}
                  {sortable && sortConfig.key === col.key && (
                    <Mui.Box component="span" sx={{ ml: 0.5 }}>
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </Mui.Box>
                  )}
                </Mui.TableCell>
              );
            })}
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {data.length === 0 ? (
            <Mui.TableRow>
              <Mui.TableCell colSpan={13} align="center">
                <People fontSize="large" sx={{ color: "gray" }} />
                <Typography variant="h6">No contacts found</Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || statusFilter !== "all" || sourceFilter !== "all" || agentFilter !== "all" || dateFilter
                    ? "No data available for the selected filter(s)"
                    : "Get started by adding your first contact"}
                </Typography>
              </Mui.TableCell>
            </Mui.TableRow>
          ) : (
            data.map((c) => (
              <Mui.TableRow hover key={c.id} sx={{ cursor: "pointer", borderLeft: `3px solid ${getColdBorderColor(c.last_activity_date)}` }} onClick={() => setSelectedContact(c)}>
                {/* Checkbox */}
                <Mui.TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(c.id)}
                    onChange={() => onSelectOne(c.id)}
                    sx={{ "&.Mui-checked": { color: crmContactColors.accent } }}
                  />
                </Mui.TableCell>
                <Mui.TableCell>{c.company || "-"}</Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Box display="flex" alignItems="center" gap={1}>
                    <Mui.Avatar src={c.image_url} sx={{ width: 28, height: 28, bgcolor: crmContactColors.action, fontSize: "0.7rem" }}>
                      {`${c.first_name?.[0] || ""}${c.last_name?.[0] || ""}`.toUpperCase()}
                    </Mui.Avatar>
                    {c.first_name || "-"}
                  </Mui.Box>
                </Mui.TableCell>
                <Mui.TableCell>{getPrimaryEmail(c) || "-"}</Mui.TableCell>
                <Mui.TableCell>{getPrimaryPhone(c) || "-"}</Mui.TableCell>
                <Mui.TableCell>{c.industry_type || "-"}</Mui.TableCell>
                <Mui.TableCell>{c.employee_count ?? "-"}</Mui.TableCell>
                <Mui.TableCell>
                  {(() => {
                    const location = getPrimaryLocation(c);
                    return location ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
                          {location}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: 12, color: "text.disabled" }}>
                        —
                      </Typography>
                    );
                  })()}
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Box display="flex" alignItems="center" gap={1}>
                    {c.agent_image_url ? (
                      <Mui.Avatar src={c.agent_image_url} sx={{ width: 24, height: 24 }} />
                    ) : (
                      <Mui.Avatar sx={{ width: 24, height: 24 }}>{c.username?.[0]?.toUpperCase()}</Mui.Avatar>
                    )}
                    {c.username || "-"}
                  </Mui.Box>
                </Mui.TableCell>
                <Mui.TableCell>
                  <Mui.Chip label={c.status_name} size="small" sx={{ backgroundColor: c.status_color, color: "#fff" }} />
                </Mui.TableCell>
                <Mui.TableCell>
                  <LastActivityCell date={c.last_activity_date} type={c.last_activity_type} />
                </Mui.TableCell>
                <Mui.TableCell>{c.createdat ? format(new Date(c.createdat), "MMM d, yyyy h:mm a") : "-"}</Mui.TableCell>
                <Mui.TableCell onClick={(e) => e.stopPropagation()}>
                  <IconButton onClick={(e) => { e.stopPropagation(); openMenu(e, c.id); }}><MoreVert /></IconButton>

                  <Menu anchorEl={menuAnchorEl} open={menuContactId === c.id} onClose={closeMenu}>
                    <MenuItem onClick={() => { history.push(`/admin/crm/contacts/${c.id}`); closeMenu(); }}>
                      <Visibility fontSize="small" sx={{ mr: 1 }} /> View profile
                    </MenuItem>
                    <MenuItem onClick={() => { closeMenu(); onLogActivity(c); }}>
                      <History fontSize="small" sx={{ mr: 1 }} /> Log activity
                    </MenuItem>
                    <MenuItem onClick={() => { closeMenu(); onLogCall(c); }}>
                      <Phone fontSize="small" sx={{ mr: 1 }} /> Log call
                    </MenuItem>
                    <Mui.Divider />
                    <MenuItem onClick={() => { onOpenEditContact(c.id); closeMenu(); }}>
                      <Edit fontSize="small" sx={{ mr: 1 }} /> Edit contact
                    </MenuItem>
                    <MenuItem onClick={(e) => { e.stopPropagation(); openRowStatusMenu(e, c.id); }}>
                      <Label fontSize="small" sx={{ mr: 1 }} /> Change status
                    </MenuItem>
                    <MenuItem onClick={(e) => { e.stopPropagation(); openRowAgentMenu(e, c.id); }}>
                      <PersonAdd fontSize="small" sx={{ mr: 1 }} /> Reassign agent
                    </MenuItem>
                    <Mui.Divider />
                    <MenuItem onClick={() => { setDeleteModal({ isOpen: true, contact: c }); closeMenu(); }} sx={{ color: "#A32D2D" }}>
                      <Delete fontSize="small" sx={{ mr: 1, color: "#A32D2D" }} /> Delete contact
                    </MenuItem>
                  </Menu>
                </Mui.TableCell>
              </Mui.TableRow>
            ))
          )}
        </Mui.TableBody>
      </Mui.Table>
      {totalCount > 0 && (
        <Mui.TablePagination component="div" rowsPerPageOptions={[10, 25, 50, 100]}
          count={totalCount} rowsPerPage={rowsPerPage} page={page}
          onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ ".MuiTablePagination-toolbar": { justifyContent: "flex-end", alignItems: "center" } }} />
      )}
    </Mui.TableContainer>
  );
};
